import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';

export default function SupplierMappingDialog({ data, onConfirmed, onCancelled }) {
  const [choices, setChoices] = useState(() =>
    Object.fromEntries(data.unmatched_suppliers.map((s) => [s, '__new__']))
  );
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    setSaving(true);
    try {
      const mappings = {};
      for (const [fileSup, choice] of Object.entries(choices)) {
        if (choice !== '__new__') mappings[fileSup] = choice;
      }
      const { data: res } = await api.post(`/purchase-upload/${data.upload_id}/confirm`, { mappings });
      onConfirmed(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    try { await api.delete(`/purchase-upload/${data.upload_id}`); } catch (e) { console.error('Failed to delete pending upload:', e); }
    onCancelled();
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !saving) cancel(); }}>
      <DialogContent data-testid="supplier-mapping-dialog" className="rounded-none sm:max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>New supplier names found</DialogTitle>
          <DialogDescription>
            These suppliers in <span className="font-medium text-zinc-700">{data.filename}</span> don't match any existing supplier.
            Map each one to an existing supplier, or confirm it as a new supplier.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
          {data.unmatched_suppliers.map((s) => (
            <div key={s} className="flex items-center gap-3">
              <span className="text-sm font-medium text-zinc-900 flex-1 truncate" title={s}>{s}</span>
              <Select value={choices[s]} onValueChange={(v) => setChoices((prev) => ({ ...prev, [s]: v }))}>
                <SelectTrigger data-testid={`supplier-map-select-${s}`} className="w-[230px] rounded-none h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none max-h-[240px]">
                  <SelectItem value="__new__" className="rounded-none text-xs font-medium text-emerald-700">
                    Add as new supplier
                  </SelectItem>
                  {data.existing_suppliers.map((es) => (
                    <SelectItem key={es} value={es} className="rounded-none text-xs">
                      Map to: {es}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button
            data-testid="supplier-mapping-cancel"
            variant="outline"
            onClick={cancel}
            disabled={saving}
            className="rounded-none border-zinc-300"
          >
            Cancel Upload
          </Button>
          <Button
            data-testid="supplier-mapping-confirm"
            onClick={confirm}
            disabled={saving}
            className="rounded-none bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? 'Importing...' : 'Confirm & Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
